import React, { useState } from "react";
import differenceBy from "lodash/differenceBy";
import { formatMessage } from "lib/globalize-util";
import { mergeStyleSets, ConfirmDialog, TextField, MessageBarType, AnimationClassNames } from "@nuorder/components";
import { FOLDERS } from "constants/integration/folders";
import { AssortmentPicker } from "../assortment-picker/assortment-picker";
import { ChooseDestinationButton } from "../utils/choose-destination-button";
import classNames from "classnames";
import { snackbar } from "containers/folders/components/snackbar";
import { IFolderSelection } from "../folder-selection/folder-navigation";
import { DialogTitle } from "../manage-access/dialog-title";
import { FolderSelection, isConfirmSelectionDisabled } from "../folder-selection/folder-selection";
import { isMyDriveEnabled } from "containers/folders/utils/permissions";
import { buildRootDrive } from "containers/folders/utils/drive-navigation";
import { useFolderSelection } from "../folder-selection/use-folder-selection";
import { MessageNotification } from "../utils/messageNotification";
import {
    useAddItem,
    useDeleteItem,
    useFetchLineageItem,
    useInfiniteFetchItems,
    useRenameItem,
} from "containers/folders/hooks/space-item";
import me from "models/me";
import { flattenInfiniteData, shouldRetryHookOnError, SpaceView } from "containers/folders/hooks/utils";
import { ItemKind, OrderByDirection, SpaceItemOrderByField } from "containers/folders/types/base";
import {
    KEY_DOT,
    MAX_ASSORTMENTS_SELECTED,
    MAX_FAILURE_COUNT,
    MAX_LINEAGE_CALLS,
    SHARED_WITH_ME_ROOT_ITEM_ID,
    _DOT_ROLL,
} from "containers/folders/utils/constants";
import { fetchMoreDelayed } from "hooks";
import { nullish } from "helpers/nullish";
import {
    CollectionType,
    ICreateRollupAssortmentRequest,
    IEditRollupAssortmentRequest,
} from "containers/folders/types/assortment";
import { useFetchAllMemberships } from "hooks/useFetchAllMemberships";
import { useCurrentMerchantId } from "hooks/useCurrentMerchantId";
import { useFetchBrand } from "containers/folders/hooks/useFetchBrand";
import { nameExtRemover, transferShortcutToCollection } from "containers/folders/utils/space-item";
import { appendDateSuffix } from "helpers/dates";
import { useCreateRollupSnapshot } from "containers/folders/hooks/server-rollups";
import { ICreateRollupSnapshotRequest } from "containers/folders/types/server-rollups";
import { useFetchSpaces } from "containers/folders/hooks/useFetchSpaces";
import { AssortmentPickerPlaceholder } from "../assortment-picker/assortment-picker-placeholder";
import { getParentItem, getParentItemWithWriteAccess } from "../utils/folder-selection";
import { hasVerticalAssortmentAccess } from "containers/folders/utils/vertical-assortments";

interface IOwnProps {
    onViewRollup?: (assortmentIds?: string[]) => void;
    onClose: () => void;
    collectionDriveItem: nuorder.folder.v1.Item;
    isEditRollupOpen?: boolean;
    hideViewRollup?: boolean;
    initialMemberships?: nuorder.merchant.v1.Member[];
    initialItemsSelected?: nuorder.folder.v1.Item[];
    onFetchSuccessRollupItems?: (items: nuorder.folder.v1.Item[]) => void;
    onFetchErrorRollupItems?: () => void;
    onSuccessCreateRollup?: () => void;
    onSuccesEditRollup?: () => void;
    parentId: string;
    needsToCreateServerRollupSnapshot?: boolean;
    onClearSnapshotCacheKey?: (value: boolean) => void;
    initialSpaceView?: SpaceView;
    initialNavigation?: Partial<nuorder.folder.v1.Item>[];
    verticalAssortmentsEnabled: boolean;
}

export const CreateRollupAssortmentDialog: React.FunctionComponent<IOwnProps> = ({
    onViewRollup,
    onClose,
    collectionDriveItem,
    isEditRollupOpen,
    hideViewRollup,
    initialMemberships = [],
    initialItemsSelected = [],
    onFetchSuccessRollupItems,
    onFetchErrorRollupItems,
    parentId,
    onSuccessCreateRollup,
    onSuccesEditRollup,
    needsToCreateServerRollupSnapshot,
    onClearSnapshotCacheKey,
    initialSpaceView,
    initialNavigation = [],
    verticalAssortmentsEnabled,
}) => {
    const classes = React.useMemo(
        () =>
            mergeStyleSets({
                hideSection: {
                    display: "none",
                },
                errorSection: {
                    marginBottom: 18,
                },
            }),
        []
    );

    const initialName = isEditRollupOpen
        ? nameExtRemover(collectionDriveItem?.name || "", collectionDriveItem?.kind as ItemKind)
        : "";
    const [name, setName] = React.useState<string>(initialName);
    const [openChooseDestination, setOpenChooseDestination] = React.useState<boolean>(false);
    const [folderDestination, setFolderDestination] = React.useState<IFolderSelection>(null);
    const [canShowErrorMessage, setCanShowErrorMessage] = React.useState<boolean>(true);
    const [rollupItems, setRollupItems] = React.useState<Partial<nuorder.folder.v1.Item>[]>([]);
    const [navigation, setNavigation] = React.useState<Partial<nuorder.folder.v1.Item>[]>(initialNavigation);
    const [itemsSelected, setItemsSelected] = React.useState<Partial<nuorder.folder.v1.Item>[]>(
        initialItemsSelected.slice(0, MAX_ASSORTMENTS_SELECTED)
    );
    const [nonVerticalAssortmentErrorMessage, setNonVerticalAssortmentErrorMessage] = useState<string>();
    let currentMemberships;
    const { memberships, isFetching: isFetchingMemberships } = useFetchAllMemberships(me._id, {
        enabled: initialMemberships.length === 0,
    });

    if (!memberships) {
        currentMemberships = initialMemberships;
    }
    const { currentMerchantId } = useCurrentMerchantId({ memberships, shouldUpdateMerchantIdInQuery: false });
    const [membership, setMembership] = React.useState<nuorder.merchant.v1.Member>(null);
    const { brand, fetchingBrand, isIdleBrandRequest } = useFetchBrand(membership);
    const { merchantSpace, mySpace, fetchingMerchantSpaces, fetchingMySpaces } = useFetchSpaces(
        membership?.merchant_id
    );

    const isVerticalAssortmentEnabled = React.useMemo(
        () => !!brand && hasVerticalAssortmentAccess(me, brand, verticalAssortmentsEnabled),
        [brand, verticalAssortmentsEnabled]
    );

    const canListChildren = React.useMemo(() => {
        if (!merchantSpace?.capabilities) {
            return fetchingMerchantSpaces ? null : false;
        }

        return merchantSpace.capabilities.can_list_children;
    }, [merchantSpace, fetchingMerchantSpaces]);

    // Set current membership and merchantId
    React.useEffect(() => {
        if (!nullish(canListChildren)) {
            if (membership?.merchant_id !== currentMerchantId) {
                const membership = memberships?.find((it) => it.merchant_id === currentMerchantId);
                setMembership(membership);
                me.setLastMerchantId(membership?.merchant_id);
            }
        }
    }, [memberships, membership, canListChildren, currentMerchantId]);

    // Fetch rollup items
    const fetchItemsEnabled = !!collectionDriveItem?.id && !!currentMerchantId && isEditRollupOpen;
    const {
        isFetching: isFetchingRollupItems,
        isFetchingMore: isFetchingMoreRollupItems,
        fetchMore: fetchMoreRollup,
        canFetchMore: canFetchMoreRollup,
    } = useInfiniteFetchItems(
        currentMerchantId,
        collectionDriveItem?.id,
        {
            order_by: SpaceItemOrderByField.Name,
            order_by_direction: OrderByDirection.Asc,
            filter: { item_kind: ItemKind.Folder, not: true },
        },
        {
            enabled: fetchItemsEnabled,
            retry: (failureCount: number, err: any) => {
                if (!shouldRetryHookOnError(err)) {
                    return false;
                }
                return failureCount <= MAX_FAILURE_COUNT;
            },
            onError: (err) => {
                onFetchErrorRollupItems && onFetchErrorRollupItems();
            },
            onSuccess: async (raw: nuorder.folder.v1.ListItemsResponse[]) => {
                const lastRaw = raw[raw.length - 1];
                // https://stackoverflow.com/questions/72753877/useinfinitequerys-enabled-option-in-react-query-doesnt-work
                if (lastRaw.next_page_token === "" && fetchItemsEnabled) {
                    const flattedRollupData = flattenInfiniteData(raw, (res) => res.items);
                    onFetchSuccessRollupItems && onFetchSuccessRollupItems(flattedRollupData);
                    setRollupItems(flattedRollupData);
                    setItemsSelected(
                        transferShortcutToCollection(flattedRollupData).slice(0, MAX_ASSORTMENTS_SELECTED)
                    );
                }
            },
        }
    );

    React.useMemo(
        () => fetchMoreDelayed(canFetchMoreRollup, isFetchingMoreRollupItems, isFetchingRollupItems, fetchMoreRollup),
        [canFetchMoreRollup, isFetchingMoreRollupItems, isFetchingRollupItems, fetchMoreRollup]
    );

    const [
        createRollupSnapshot,
        {
            error: createRollupSnapshotError,
            isLoading: isCreateRollupSnapshotLoading,
            reset: resetCreateRollupSnapshot,
        },
    ] = useCreateRollupSnapshot({
        onError: (_err: any, params: ICreateRollupSnapshotRequest) => {
            if (needsToCreateServerRollupSnapshot) {
                snackbar.show(formatMessage("There was an error opening your advanced rollup"));
                return;
            }
            snackbar.show(
                formatMessage("There was an error creating your advanced rollup for |item|", {
                    item: nameExtRemover(params.rollupFolder.name, params.rollupFolder.kind as ItemKind),
                })
            );
        },
    });
    const [addItem, { error: addError, isLoading: isAddLoading, reset: resetAdd }] = useAddItem();
    const [deleteItem, { error: deleteError, isLoading: isDeleteLoading, reset: resetDeleteItem }] = useDeleteItem();
    const [renameItem, { error: renameError, isLoading: isRenameLoading, reset: resetRename }] = useRenameItem();
    const errorMessage =
        addError?.message || renameError?.message || deleteError?.message || createRollupSnapshotError?.message || "";
    const isProcessing = isAddLoading || isDeleteLoading || isRenameLoading || isCreateRollupSnapshotLoading;
    const hasDriveModuleEnabled = React.useMemo(() => {
        if (!brand) {
            return fetchingBrand || isIdleBrandRequest ? null : false;
        }

        return brand?.modules?.drive?.enabled || false;
    }, [fetchingBrand, brand, isIdleBrandRequest]);
    const userDefaultView = !me.isBrandUser() || hasDriveModuleEnabled ? SpaceView.MyDrive : SpaceView.SharedWithMe;
    const spaceView = initialSpaceView ?? (canListChildren ? SpaceView.MerchantDrive : userDefaultView);
    const currentSpace =
        spaceView === SpaceView.MerchantDrive ? merchantSpace : spaceView === SpaceView.MyDrive ? mySpace : null;
    const rootItemId = spaceView !== SpaceView.SharedWithMe ? currentSpace?.root_item_id : SHARED_WITH_ME_ROOT_ITEM_ID;

    // Fetch lineage's item for breadcrumbs(navigation) only when there is not initialNavigation
    const { isFetching: isFetchingLineage } = useFetchLineageItem(
        membership?.merchant_id,
        parentId,
        rootItemId,
        MAX_LINEAGE_CALLS,
        false,
        {
            enabled: membership?.merchant_id && parentId && navigation.length === 0 && initialNavigation.length === 0,
            retry: (failureCount: number, err: any) => {
                if (!shouldRetryHookOnError(err)) {
                    return false;
                }
                return failureCount <= MAX_FAILURE_COUNT;
            },
            onSettled: (response: Partial<nuorder.folder.v1.Item>[], error: any) => {
                if (error) {
                    setNavigation([]);
                } else {
                    setNavigation(response);
                }
            },
        }
    );

    // Set initial settings for choose destination
    const [settings, setSettings] = useFolderSelection({
        currentItemId: parentId,
        rootItemId,
        currentView: spaceView,
        navigation: [
            buildRootDrive(currentSpace, spaceView, membership?.merchant_ref?.name ?? membership?.merchant_id),
            ...(navigation as IFolderSelection[]),
        ],
    });
    // Update `settings` with latest rootItemId for choose destination
    React.useEffect(() => {
        if (rootItemId && settings.rootItemId !== rootItemId && currentSpace && spaceView && membership) {
            setSettings({
                ...settings,
                rootItemId,
                navigation: [
                    buildRootDrive(currentSpace, spaceView, membership?.merchant_ref?.name ?? membership?.merchant_id),
                    ...(navigation as IFolderSelection[]),
                ],
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rootItemId, settings, navigation, currentSpace, spaceView, membership]);

    const displayMyDrive = React.useMemo(
        () => isMyDriveEnabled(brand, verticalAssortmentsEnabled),
        [brand, verticalAssortmentsEnabled]
    );
    const isSelectionDisabled = React.useMemo(
        () => isConfirmSelectionDisabled(settings, null, false, true, true),
        [settings]
    );
    const isConfirmDisabled = React.useMemo(() => {
        if (openChooseDestination) {
            return isSelectionDisabled;
        }

        return !folderDestination || folderDestination.spaceView === SpaceView.SharedWithMe;
    }, [openChooseDestination, folderDestination, isSelectionDisabled]);

    const getFolderDestination = React.useCallback(
        (_settings) => {
            if (isSelectionDisabled) {
                return null;
            }

            const { itemSelected, navigation } = _settings;
            return itemSelected || navigation[navigation.length - 1];
        },
        [isSelectionDisabled]
    );

    const handleChangeName = React.useCallback(
        (_e, newValue) => {
            if (errorMessage) {
                resetRename();
                resetAdd();
            }

            setName(newValue);
        },
        [errorMessage, resetRename, resetAdd]
    );

    const handleViewRollup = React.useCallback(() => {
        onViewRollup && onViewRollup(itemsSelected.map((assortment) => assortment.document_id));
    }, [itemsSelected, onViewRollup]);

    const handleKeyDown = React.useCallback((event) => {
        if (event.keyCode === KEY_DOT) {
            event.preventDefault();
        }
    }, []);

    const handleOnPaste = React.useCallback((e: React.ClipboardEvent) => {
        const clipboardData = e.clipboardData;
        const pastedText = clipboardData.getData("Text");
        if (pastedText.indexOf(".") !== -1) {
            e.preventDefault();
        }
    }, []);

    const handleOpenChooseDestination = React.useCallback(() => {
        setOpenChooseDestination(true);
    }, [setOpenChooseDestination]);

    const handleCloseChooseDestination = React.useCallback(() => {
        setOpenChooseDestination(false);
        const selection = getFolderDestination(settings);
        selection && setFolderDestination(selection);
    }, [setOpenChooseDestination, getFolderDestination, settings]);

    const handleConfirmCreateRollup = React.useCallback(
        async (params: ICreateRollupAssortmentRequest) => {
            if (collectionDriveItem) {
                const response = await addItem({
                    merchantId: params.merchantId,
                    rootId: params.parentId,
                    name: `${params.name}${_DOT_ROLL}`,
                    kind: ItemKind.Folder,
                });

                if (response) {
                    await createRollupSnapshot({
                        merchantId: params.merchantId,
                        parentId: response.id,
                        collectionIds: params.assortments.map(({ document_id }) => document_id),
                        name: appendDateSuffix(response.id, true),
                        rollupFolder: response,
                    });

                    resetCreateRollupSnapshot();

                    for (const [_index, assortment] of params.assortments.entries()) {
                        await addItem({
                            merchantId: params.merchantId,
                            rootId: response.id,
                            item: assortment,
                            kind: ItemKind.Shortcut,
                            targetItemId: assortment.id,
                            isShortcutItem: true,
                            belongsToRollupItem: true,
                        });
                    }

                    resetAdd();
                    if (onSuccessCreateRollup) {
                        onSuccessCreateRollup();
                    }
                    snackbar.show(
                        formatMessage("Your Assortment Rollup has been saved to |destination|.", {
                            destination: params.parentName,
                        })
                    );
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [collectionDriveItem]
    );

    const handleConfirmEditRollup = React.useCallback(
        async (params: IEditRollupAssortmentRequest) => {
            if (collectionDriveItem) {
                try {
                    let itemRenamed;
                    const itemsAdded = [];
                    const itemsDeleted = [];
                    const [itemsToAdd, itemsToDelete] = generateRollupUpdate(
                        transferShortcutToCollection(rollupItems),
                        params.assortments
                    );
                    if (itemsToAdd.length || itemsToDelete.length) {
                        await createRollupSnapshot({
                            merchantId: params.merchantId,
                            parentId: collectionDriveItem.id,
                            collectionIds: params.assortments.map(({ document_id }) => document_id),
                            name: appendDateSuffix(collectionDriveItem.id, true),
                            rollupFolder: collectionDriveItem,
                        });

                        resetCreateRollupSnapshot();
                        onClearSnapshotCacheKey && onClearSnapshotCacheKey(true);
                    }
                    if (itemsToAdd.length) {
                        for (const itemToAdd of itemsToAdd) {
                            const itemAdded = await addItem({
                                merchantId: params.merchantId,
                                rootId: collectionDriveItem.id,
                                kind: ItemKind.Shortcut,
                                targetItemId: itemToAdd.id,
                                item: itemToAdd,
                                isShortcutItem: true,
                                belongsToRollupItem: true,
                            });
                            itemsAdded.push(itemAdded);
                        }
                    }
                    if (itemsToDelete.length) {
                        for (const itemToDelete of itemsToDelete) {
                            const itemDeleted = await deleteItem({
                                merchantId: params.merchantId,
                                itemId: (itemToDelete as any).shortcut_id,
                            });
                            itemsDeleted.push(itemDeleted);
                        }
                    }

                    if (initialName !== params.name) {
                        itemRenamed = await renameItem({
                            merchantId: currentMerchantId,
                            itemId: collectionDriveItem.id,
                            name: `${params.name}${_DOT_ROLL}`,
                        });
                        if (!itemRenamed) {
                            return;
                        }
                    }
                    const addAll = itemsAdded.length === 0 || !itemsAdded.some((res) => !res);
                    const deleteAll = itemsDeleted.length === 0 || !itemsDeleted.some((res) => !res);
                    if (addAll && deleteAll) {
                        resetRename();
                        resetDeleteItem();
                        resetAdd();
                    }

                    if (onSuccesEditRollup) {
                        onSuccesEditRollup();
                    }
                    snackbar.show(formatMessage("Your Assortment Rollup has been updated."));
                } catch (e) {
                    // react query handle error case.
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [collectionDriveItem, initialName, resetAdd, rollupItems, onClearSnapshotCacheKey]
    );

    const handleCreateRollup = React.useCallback(() => {
        const params = {
            name,
            assortments: itemsSelected,
            merchantId: currentMerchantId,
            parentId: folderDestination?.id,
            parentName: folderDestination?.name,
        };

        if (isEditRollupOpen) {
            handleConfirmEditRollup(params);
        } else {
            handleConfirmCreateRollup(params);
        }
        setCanShowErrorMessage(true);
    }, [
        itemsSelected,
        currentMerchantId,
        name,
        setCanShowErrorMessage,
        folderDestination,
        isEditRollupOpen,
        handleConfirmCreateRollup,
        handleConfirmEditRollup,
    ]);

    const generateRollupUpdate = React.useCallback(
        (initialItems: nuorder.folder.v1.Item[], currentItems: nuorder.folder.v1.Item[]) => {
            const itemsToSave = differenceBy(currentItems, initialItems, "id");
            const itemsToDelete = differenceBy(initialItems, currentItems, "id");
            return [itemsToSave, itemsToDelete];
        },
        []
    );

    // Only for create rollup
    React.useEffect(() => {
        if (!folderDestination && collectionDriveItem && membership && !fetchingMerchantSpaces && !fetchingMySpaces) {
            const currentParent = getParentItem(
                collectionDriveItem as IFolderSelection,
                merchantSpace,
                mySpace,
                membership
            );
            const parentItem = getParentItemWithWriteAccess(currentParent, mySpace);
            setFolderDestination(parentItem as IFolderSelection);
            if (parentItem.id !== parentId) {
                // Change settings to my drive if you don't have access
                setSettings({
                    currentView: SpaceView.MyDrive,
                    rootItemId: mySpace?.root_item_id,
                    currentItemId: mySpace?.root_item_id,
                    navigation: [
                        buildRootDrive(
                            mySpace,
                            SpaceView.MyDrive,
                            membership?.merchant_ref?.name ?? membership?.merchant_id
                        ),
                    ],
                });
            }
        }
    }, [
        collectionDriveItem,
        merchantSpace,
        mySpace,
        membership,
        folderDestination,
        setFolderDestination,
        setSettings,
        parentId,
        currentSpace,
        fetchingMerchantSpaces,
        fetchingMySpaces,
    ]);

    const handleCloseErrorAlert = () => {
        setCanShowErrorMessage(false);
        if (isVerticalAssortmentEnabled) {
            setNonVerticalAssortmentErrorMessage(undefined);
        }
    };

    const headerTitle = isEditRollupOpen ? formatMessage("Edit Rollup") : formatMessage("New Rollup");
    const assortmentsDeletedError = React.useMemo(() => {
        return itemsSelected.some((it) => it.shortcut_facet?.target_item_deleted)
            ? formatMessage("One or more assortments are unable to load. Please remove and resave.")
            : null;
    }, [itemsSelected]);

    const isFetchingDataForAssortmentSelector =
        isFetchingMemberships || isFetchingRollupItems || canFetchMoreRollup || isFetchingLineage;

    const isNonVerticalAssortmentSelected = itemsSelected.some(
        (item) => item.collection_facet?.collection_type !== CollectionType.Vertical
    );
    const isAssortmentsDeletedError = !!assortmentsDeletedError;
    const hasErrorMessage = !!errorMessage;
    const hasInvalidSelection = openChooseDestination ? isConfirmDisabled : itemsSelected?.length < 2 || !name;
    const hasNonVerticalAssortment = isVerticalAssortmentEnabled && isNonVerticalAssortmentSelected;

    const isConfirmButtonDisabled = React.useMemo(
        () =>
            isProcessing ||
            isAssortmentsDeletedError ||
            hasErrorMessage ||
            hasInvalidSelection ||
            hasNonVerticalAssortment,
        [isProcessing, isAssortmentsDeletedError, hasErrorMessage, hasInvalidSelection, hasNonVerticalAssortment]
    );

    React.useEffect(() => {
        if (isVerticalAssortmentEnabled) {
            if (isNonVerticalAssortmentSelected) {
                setCanShowErrorMessage(true);

                const nonVerticalAssortmentError = formatMessage(
                    "One or more assortments are unable to be included in this rollup. Please remove before saving."
                );

                setNonVerticalAssortmentErrorMessage(nonVerticalAssortmentError);
            } else {
                setNonVerticalAssortmentErrorMessage(undefined);
            }
        }
    }, [isNonVerticalAssortmentSelected, isVerticalAssortmentEnabled]);

    return (
        <ConfirmDialog
            contentProps={{
                title: (
                    <DialogTitle
                        iconName={openChooseDestination ? "arrowLeft24" : ""}
                        mainTitle={!openChooseDestination ? headerTitle : formatMessage("Choose destination")}
                        onBack={handleCloseChooseDestination}
                    />
                ) as any,
            }}
            onConfirm={openChooseDestination ? handleCloseChooseDestination : handleCreateRollup}
            onClose={onClose}
            open={true}
            isLoading={isProcessing}
            confirmButtonDisabled={isConfirmButtonDisabled}
            data-test={FOLDERS.CREATE_ROLLUP_ASSORTMENT_DIALOG.CONTAINER}
            okLabel={!openChooseDestination ? formatMessage("Save Rollup") : formatMessage("Select")}
            customLeftLabel={
                !hideViewRollup && !openChooseDestination && me.isNuOrderUser() && formatMessage("View Rollup")
            }
            onLeftActionClicked={handleViewRollup}
            leftButtonDisabled={isProcessing || itemsSelected?.length < 2}
            width={600}
            hideCancelButton={openChooseDestination}
            cancelLabel={formatMessage("Cancel")}
            closeButtonAriaLabel={formatMessage("Close")}
            scrollable={false}
        >
            <form onSubmit={handleCreateRollup}>
                <div
                    className={classNames({
                        [classes.hideSection]: openChooseDestination,
                        [AnimationClassNames.slideRightIn400]: !openChooseDestination,
                    })}
                >
                    {(errorMessage || assortmentsDeletedError || nonVerticalAssortmentErrorMessage) &&
                        canShowErrorMessage && (
                            <MessageNotification
                                message={errorMessage || assortmentsDeletedError || nonVerticalAssortmentErrorMessage}
                                dataTest={FOLDERS.CREATE_ROLLUP_ASSORTMENT_DIALOG.MESSAGE_ERROR}
                                messageBarType={MessageBarType.error}
                                onDismiss={handleCloseErrorAlert}
                                className={classes.errorSection}
                            />
                        )}
                    {!isEditRollupOpen && (
                        <ChooseDestinationButton text={folderDestination?.name} onClick={handleOpenChooseDestination} />
                    )}
                    <TextField
                        data-test={FOLDERS.CREATE_ROLLUP_ASSORTMENT_DIALOG.NAME}
                        value={name}
                        required
                        onChange={handleChangeName}
                        label={formatMessage("Name:")}
                        disabled={isProcessing}
                        onKeyDown={handleKeyDown}
                        onPaste={handleOnPaste}
                    />
                    {isFetchingDataForAssortmentSelector ? (
                        <AssortmentPickerPlaceholder />
                    ) : (
                        <AssortmentPicker
                            itemsSelected={itemsSelected}
                            mySpace={mySpace}
                            merchantSpace={merchantSpace}
                            displayMerchantDrive={canListChildren}
                            brand={brand}
                            membership={membership}
                            spaceView={spaceView}
                            spaceItemNavigation={navigation}
                            onChangeSelection={setItemsSelected}
                            parentId={parentId}
                            itemLimit={MAX_ASSORTMENTS_SELECTED}
                            verticalAssortmentsEnabled={isVerticalAssortmentEnabled}
                            displayMyDrive={displayMyDrive}
                        />
                    )}
                </div>
                <div
                    className={classNames({
                        [classes.hideSection]: !openChooseDestination,
                        [AnimationClassNames.slideLeftIn400]: openChooseDestination,
                    })}
                >
                    {openChooseDestination && (
                        <FolderSelection
                            displayMerchantDrive={canListChildren}
                            displayMyDrive={displayMyDrive}
                            membership={membership}
                            mySpace={mySpace}
                            merchantSpace={merchantSpace}
                            item={null}
                            settings={settings}
                            onUpdateSettings={setSettings}
                            noMargin={true}
                        />
                    )}
                </div>
            </form>
        </ConfirmDialog>
    );
};
